(() => {
  'use strict';

  if (window.NuoraGrV3) return;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const initFacebook = (root) => {
    if (!root || root.dataset.grV3Ready === 'true') return;
    root.dataset.grV3Ready = 'true';

    const track = root.querySelector('[data-gr-v3-facebook-track]');
    const cards = Array.from(root.querySelectorAll('[data-gr-v3-facebook-card]'));
    const previous = root.querySelector('[data-gr-v3-facebook-prev]');
    const next = root.querySelector('[data-gr-v3-facebook-next]');
    const progress = root.querySelector('[data-gr-v3-facebook-progress]');
    const count = root.querySelector('[data-gr-v3-facebook-count]');
    const status = root.querySelector('[data-gr-v3-facebook-status]');
    const dots = Array.from(root.querySelectorAll('[data-gr-v3-facebook-dot]'));
    let activeIndex = 0;
    let frame = 0;
    let resizeFrame = 0;

    const normalizeHeights = () => {
      if (!track || !cards.length) return;
      cards.forEach((card) => { card.style.height = 'auto'; });
      if (window.matchMedia('(min-width: 990px)').matches) {
        track.style.height = 'auto';
        return;
      }
      const maxHeight = Math.ceil(Math.max(...cards.map((card) => card.getBoundingClientRect().height)));
      if (!maxHeight) return;
      cards.forEach((card) => { card.style.height = `${maxHeight}px`; });
      track.style.height = `${maxHeight}px`;
    };

    const update = (index) => {
      activeIndex = clamp(index, 0, cards.length - 1);
      if (previous) previous.disabled = activeIndex === 0;
      if (next) next.disabled = activeIndex === cards.length - 1;
      if (progress) progress.style.width = `${((activeIndex + 1) / cards.length) * 100}%`;
      if (count) count.textContent = `${activeIndex + 1} / ${cards.length}`;
      if (status) {
        const prefix = root.dataset.statusPrefix || 'Showing conversation';
        const separator = root.dataset.statusSeparator || 'of';
        status.textContent = `${prefix} ${activeIndex + 1} ${separator} ${cards.length}`;
      }
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === activeIndex;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    const goTo = (index) => {
      const targetIndex = clamp(index, 0, cards.length - 1);
      const card = cards[targetIndex];
      if (!track || !card) return;
      const centeredLeft = card.offsetLeft - ((track.clientWidth - card.offsetWidth) / 2);
      track.scrollTo({
        left: Math.max(0, centeredLeft),
        behavior: reduceMotion() ? 'auto' : 'smooth',
      });
      update(targetIndex);
    };

    previous?.addEventListener('click', () => goTo(activeIndex - 1));
    next?.addEventListener('click', () => goTo(activeIndex + 1));
    dots.forEach((dot, index) => dot.addEventListener('click', () => goTo(index)));
    track?.addEventListener('scroll', () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const left = track.getBoundingClientRect().left;
        let closest = 0;
        let distance = Number.POSITIVE_INFINITY;
        cards.forEach((card, index) => {
          const nextDistance = Math.abs(card.getBoundingClientRect().left - left);
          if (nextDistance < distance) {
            closest = index;
            distance = nextDistance;
          }
        });
        update(closest);
      });
    }, { passive: true });

    const queueNormalize = () => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0;
        normalizeHeights();
      });
    };

    root.querySelectorAll('img').forEach((image) => {
      if (!image.complete) image.addEventListener('load', queueNormalize, { once: true });
    });
    window.addEventListener('resize', queueNormalize, { passive: true });
    normalizeHeights();
    update(0);
  };

  const initAccordion = (root) => {
    if (!root || root.dataset.grV3Ready === 'true') return;
    root.dataset.grV3Ready = 'true';
    const items = Array.from(root.querySelectorAll('details'));
    items.forEach((item) => {
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        items.forEach((other) => {
          if (other !== item) other.open = false;
        });
      });
    });
  };

  const initFooter = (root) => {
    if (!root || root.dataset.grV3FooterReady === 'true') return;
    root.dataset.grV3FooterReady = 'true';

    const desktopQuery = window.matchMedia('(min-width: 750px)');
    const menus = Array.from(root.querySelectorAll('.nuora-footer-390__menu'));

    const sync = () => {
      menus.forEach((menu) => {
        const summary = menu.querySelector('summary');
        const shouldOpen = desktopQuery.matches || menu.dataset.mobileOpen === 'true';
        if (menu.open !== shouldOpen) menu.open = shouldOpen;
        if (summary) summary.tabIndex = desktopQuery.matches ? -1 : 0;
      });
    };

    const preventDesktopToggle = (event) => {
      if (desktopQuery.matches) event.preventDefault();
    };

    menus.forEach((menu) => {
      menu.querySelector('summary')?.addEventListener('click', preventDesktopToggle);
      menu.addEventListener('toggle', () => {
        if (!desktopQuery.matches) menu.dataset.mobileOpen = String(menu.open);
      });
    });

    desktopQuery.addEventListener?.('change', sync);
    sync();

    root.grV3FooterCleanup = () => {
      desktopQuery.removeEventListener?.('change', sync);
      menus.forEach((menu) => {
        menu.querySelector('summary')?.removeEventListener('click', preventDesktopToggle);
      });
    };
  };

  const initProgress = (root) => {
    if (!root || root.dataset.grV3Ready === 'true') return;
    root.dataset.grV3Ready = 'true';
    const track = root.querySelector('[data-gr-v3-progress-track]');
    const steps = Array.from(root.querySelectorAll('[data-gr-v3-progress-step]'));
    if (!track || !steps.length) return;
    let activeIndex = 0;
    let geometryFrame = 0;

    const syncGeometry = () => {
      if (steps.length < 2) return;
      if (geometryFrame) cancelAnimationFrame(geometryFrame);
      geometryFrame = requestAnimationFrame(() => {
        geometryFrame = 0;
        const trackRect = track.getBoundingClientRect();
        const firstDot = steps[0].querySelector('.nuora-resilia-journey__dot')?.getBoundingClientRect();
        const lastDot = steps[steps.length - 1].querySelector('.nuora-resilia-journey__dot')?.getBoundingClientRect();
        if (!firstDot || !lastDot) return;
        const lineTop = firstDot.top + (firstDot.height / 2) - trackRect.top;
        const lineEnd = lastDot.top + (lastDot.height / 2) - trackRect.top;
        const lineHeight = Math.max(0, lineEnd - lineTop);
        track.style.setProperty('--journey-line-top', `${lineTop}px`);
        track.style.setProperty('--journey-line-height', `${lineHeight}px`);
        track.style.setProperty('--journey-progress-height', `${lineHeight * (activeIndex / (steps.length - 1))}px`);
      });
    };

    const activate = (index) => {
      activeIndex = clamp(index, 0, steps.length - 1);
      steps.forEach((step, stepIndex) => step.classList.toggle('is-active', stepIndex <= index));
      syncGeometry();
    };

    if (!('IntersectionObserver' in window) || reduceMotion()) {
      activate(steps.length - 1);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const index = steps.indexOf(entry.target);
        if (index >= 0) activate(index);
      });
    }, { rootMargin: '-20% 0px -55% 0px', threshold: 0.05 });

    steps.forEach((step) => observer.observe(step));
    root.grV3Observer = observer;
    window.addEventListener('resize', syncGeometry, { passive: true });
    activate(0);
  };

  const initReviewGrid = (root) => {
    if (!root || root.dataset.grV3Ready === 'true') return;
    root.dataset.grV3Ready = 'true';

    const cards = Array.from(root.querySelectorAll('[data-gr-v3-review-card]'));
    const filterToggle = root.querySelector('[data-gr-v3-review-filter-toggle]');
    const filters = root.querySelector('[data-gr-v3-review-filters]');
    const filterButtons = Array.from(root.querySelectorAll('[data-gr-v3-review-rating]'));
    const more = root.querySelector('[data-gr-v3-review-more]');
    const empty = root.querySelector('[data-gr-v3-review-empty]');
    const status = root.querySelector('[data-gr-v3-review-status]');
    const grid = root.querySelector('[data-gr-v3-review-cards]');
    const initialCount = Number(root.dataset.initialCount) || 8;
    const batchSize = Number(root.dataset.batchSize) || 4;
    let rating = 'all';
    let visibleCount = initialCount;
    let masonryFrame = 0;

    const layoutMasonry = () => {
      if (!grid) return;
      const styles = getComputedStyle(grid);
      const rowHeight = Number.parseFloat(styles.gridAutoRows) || 4;
      const gap = Number.parseFloat(styles.rowGap) || 8;
      cards.forEach((card) => {
        card.style.gridRowEnd = 'auto';
        if (card.hidden) return;
        const height = card.getBoundingClientRect().height;
        card.style.gridRowEnd = `span ${Math.ceil((height + gap) / (rowHeight + gap))}`;
      });
    };

    const scheduleMasonry = () => {
      if (masonryFrame) cancelAnimationFrame(masonryFrame);
      masonryFrame = requestAnimationFrame(() => {
        masonryFrame = requestAnimationFrame(() => {
          masonryFrame = 0;
          layoutMasonry();
        });
      });
    };

    const matchingCards = () => cards.filter((card) => rating === 'all' || card.dataset.rating === rating);

    const render = () => {
      const matching = matchingCards();
      cards.forEach((card) => { card.hidden = true; });
      matching.slice(0, visibleCount).forEach((card) => { card.hidden = false; });
      const shown = Math.min(visibleCount, matching.length);
      if (status) {
        const showingLabel = root.dataset.showingLabel || 'Showing';
        const ofLabel = root.dataset.ofLabel || 'of';
        const reviewsLabel = root.dataset.reviewsLabel || 'reviews';
        status.textContent = `${showingLabel} ${shown} ${ofLabel} ${matching.length} ${reviewsLabel}`;
      }
      if (empty) empty.hidden = matching.length !== 0;
      if (more) more.hidden = shown >= matching.length;
      scheduleMasonry();
    };

    filterToggle?.addEventListener('click', () => {
      const expanded = filterToggle.getAttribute('aria-expanded') !== 'true';
      filterToggle.setAttribute('aria-expanded', String(expanded));
      if (filters) filters.hidden = !expanded;
    });

    filterButtons.forEach((button) => {
      button.addEventListener('click', () => {
        rating = button.dataset.grV3ReviewRating || 'all';
        visibleCount = initialCount;
        filterButtons.forEach((candidate) => {
          const active = candidate === button;
          candidate.classList.toggle('is-active', active);
          candidate.setAttribute('aria-pressed', String(active));
        });
        render();
      });
    });

    more?.addEventListener('click', () => {
      visibleCount += batchSize;
      render();
    });

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-gr-v3-review-expand]');
      if (!button) return;
      const card = button.closest('[data-gr-v3-review-card]');
      const expanded = card?.classList.toggle('is-expanded') || false;
      button.setAttribute('aria-expanded', String(expanded));
      button.textContent = expanded ? button.dataset.collapseLabel : button.dataset.expandLabel;
      scheduleMasonry();
    });

    root.querySelectorAll('img').forEach((image) => {
      if (!image.complete) image.addEventListener('load', scheduleMasonry, { once: true });
    });
    window.addEventListener('resize', scheduleMasonry, { passive: true });

    render();
  };

  const initAmazonReviews = (root) => {
    if (!root || root.dataset.grV3AmazonReady === 'true') return;
    const cards = Array.from(root.querySelectorAll('[data-review-card]'));
    const buttons = Array.from(root.querySelectorAll('[data-review-rating-filter]'));
    const more = root.querySelector('[data-review-more]');
    const empty = root.querySelector('[data-review-empty]');
    const status = root.querySelector('[data-review-rating-status]');
    if (!cards.length || !buttons.length) return;
    root.dataset.grV3AmazonReady = 'true';

    const initialCount = Math.max(1, Number(root.dataset.initialCount) || 3);
    const batchSize = Math.max(1, Number(root.dataset.batchSize) || 6);
    let activeRating = '5';
    let visibleCount = initialCount;

    const render = () => {
      const matching = cards.filter((card) => activeRating === 'all' || card.dataset.reviewRating === activeRating);
      const visible = new Set(matching.slice(0, visibleCount));
      cards.forEach((card) => { card.hidden = !visible.has(card); });
      buttons.forEach((button) => {
        const selected = button.dataset.reviewRatingFilter === activeRating;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
        const rating = button.dataset.reviewRatingFilter;
        const selectedSuffix = root.dataset.selectedActionLabel || 'star reviews selected. Activate again to show all reviews.';
        const showOnlyPrefix = root.dataset.showOnlyLabel || 'Show only';
        button.setAttribute('aria-label', selected
          ? `${rating}-${selectedSuffix}`
          : `${showOnlyPrefix} ${rating}-star reviews.`);
      });
      if (status) {
        status.textContent = activeRating === 'all'
          ? (root.dataset.allRatingsLabel || 'All ratings shown')
          : `${activeRating} ${root.dataset.starsSelectedLabel || 'stars selected'}`;
      }
      if (empty) empty.hidden = matching.length > 0;
      if (more) {
        more.hidden = matching.length <= visibleCount;
        const allLabel = root.dataset.moreAllLabel || `SHOW ${batchSize} MORE REVIEWS`;
        const prefix = root.dataset.moreRatingPrefix || 'SHOW MORE';
        const suffix = root.dataset.moreRatingSuffix || 'STAR REVIEWS';
        more.textContent = activeRating === 'all' ? allLabel : `${prefix} ${activeRating}-${suffix}`;
      }
    };

    buttons.forEach((button) => button.addEventListener('click', () => {
      const rating = button.dataset.reviewRatingFilter || '5';
      activeRating = activeRating === rating ? 'all' : rating;
      visibleCount = initialCount;
      render();
    }));
    more?.addEventListener('click', () => {
      visibleCount += batchSize;
      render();
    });
    render();
  };

  const initIngredients = (root) => {
    if (!root || root.dataset.grV3IngredientsReady === 'true') return;
    const dialog = root.querySelector('[data-gr-v3-ingredient-dialog]');
    const triggers = Array.from(root.querySelectorAll('[data-gr-v3-ingredient-open]'));
    if (!dialog || !triggers.length) return;
    root.dataset.grV3IngredientsReady = 'true';

    const eyebrow = dialog.querySelector('[data-gr-v3-ingredient-eyebrow]');
    const identity = dialog.querySelector('[data-gr-v3-ingredient-identity]');
    const image = dialog.querySelector('[data-gr-v3-ingredient-image]');
    const title = dialog.querySelector('[data-gr-v3-ingredient-title]');
    const badges = dialog.querySelector('[data-gr-v3-ingredient-badges]');
    const description = dialog.querySelector('[data-gr-v3-ingredient-description]');
    const benefits = dialog.querySelector('[data-gr-v3-ingredient-benefits]');
    const meta = dialog.querySelector('[data-gr-v3-ingredient-meta]');
    let activeTrigger = null;
    let lastInputMethod = 'pointer';

    root.addEventListener('pointerdown', () => { lastInputMethod = 'pointer'; }, { passive: true });
    root.addEventListener('keydown', () => { lastInputMethod = 'keyboard'; });

    const reset = () => {
      triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.closest('[data-ingredient-card]')?.classList.remove('is-selected');
      });
      document.documentElement.classList.remove('nuora-ingredient-modal-open');
    };

    const close = () => {
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      else {
        dialog.removeAttribute('open');
        reset();
      }
    };

    const open = (trigger) => {
      const card = trigger.closest('[data-ingredient-card]');
      if (!card) return;
      const role = card.querySelector('.nuora-ingredients__role')?.textContent.trim() || 'Ingredient';
      const sourceImage = card.querySelector('.nuora-ingredients__visual img');
      const cardBadges = Array.from(card.querySelectorAll('.nuora-ingredients__badges > span')).slice(0, 2);
      const cardBenefits = Array.from(card.querySelectorAll('.nuora-ingredients__benefits li')).slice(0, 2);

      reset();
      activeTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
      card.classList.add('is-selected');
      if (eyebrow) eyebrow.textContent = card.hasAttribute('data-research-card') ? 'WHY IT MATTERS' : 'WHY IT’S INCLUDED';
      if (title) title.textContent = role;
      if (description) description.textContent = card.querySelector('.nuora-ingredients__description')?.textContent.trim() || '';
      if (meta) meta.textContent = card.querySelector('.nuora-ingredients__footer')?.textContent.trim() || '';
      if (image) {
        image.hidden = !sourceImage;
        image.src = sourceImage?.currentSrc || sourceImage?.src || image.src;
        image.alt = sourceImage ? `${role} ingredient` : '';
      }
      identity?.classList.toggle('is-text-only', !sourceImage);
      if (badges) badges.replaceChildren(...cardBadges.map((badge) => {
        const chip = document.createElement('span');
        chip.textContent = badge.textContent.trim();
        return chip;
      }));
      if (benefits) benefits.replaceChildren(...cardBenefits.map((benefit) => {
        const item = document.createElement('li');
        item.textContent = benefit.textContent.trim();
        return item;
      }));

      document.documentElement.classList.add('nuora-ingredient-modal-open');
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    };

    triggers.forEach((trigger) => trigger.addEventListener('click', () => open(trigger)));
    dialog.querySelectorAll('[data-gr-v3-ingredient-close]').forEach((button) => button.addEventListener('click', close));
    dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
    dialog.addEventListener('close', () => {
      const trigger = activeTrigger;
      reset();
      if (lastInputMethod === 'keyboard') trigger?.focus({ preventScroll: true });
      else requestAnimationFrame(() => trigger?.blur());
      activeTrigger = null;
    });
  };

  const initOfferSignals = (root) => {
    if (!root || root.dataset.nuoraSignalsReady === 'true') return;
    const module = root.querySelector('[data-nuora-offer-scarcity]');
    if (!module) return;

    root.dataset.nuoraSignalsReady = 'true';
    const stockCount = module.querySelector('[data-nuora-stock-count]');
    const stockBar = module.querySelector('[data-nuora-stock-bar]');
    const stockTrack = module.querySelector('[data-nuora-stock-track]');
    const viewerCount = module.querySelector('[data-nuora-viewer-count]');
    const stockStatus = module.querySelector('[data-nuora-stock-status]');
    const stockPrefix = module.querySelector('[data-nuora-stock-prefix]');
    const stockSuffix = module.querySelector('[data-nuora-stock-suffix]');
    const announcement = module.querySelector('[data-nuora-stock-announcement]');
    const signals = window.NuoraOfferSignals || null;
    const hasLiveSignals = Boolean(
      signals
      && Number.isFinite(Number(signals.stock))
      && Number.isFinite(Number(signals.viewers))
    );
    let stock = hasLiveSignals ? Number(signals.stock) : Number(root.dataset.nuoraStock) || 29;
    let viewers = hasLiveSignals ? Number(signals.viewers) : Number(root.dataset.nuoraViewers) || 17;
    let stockMaximum = hasLiveSignals && Number.isFinite(Number(signals.stockMaximum))
      ? Math.max(stock, Number(signals.stockMaximum))
      : Math.max(stock, Number(root.dataset.nuoraStockMaximum) || 42);
    const stockFloor = 13;
    const previewRedThreshold = Math.max(stockFloor, Math.round(stockMaximum * .5));
    const viewerMaximum = Math.max(viewers, Number(root.dataset.nuoraViewerMaximum) || 22);
    const animateSignals = root.dataset.nuoraAnimateSignals === 'true';
    const motionReduced = reduceMotion();
    let visible = false;
    let engaged = false;
    let stockTimer = 0;
    let viewerTimer = 0;
    let unsubscribe = null;

    const animateNumber = (element, value) => {
      if (!element || element.textContent === String(value)) return;
      element.textContent = String(value);
      if (motionReduced) return;
      element.classList.remove('nuora-offer-scarcity__number-changing');
      void element.offsetWidth;
      element.classList.add('nuora-offer-scarcity__number-changing');
    };

    const renderStock = (announce = false) => {
      animateNumber(stockCount, stock);
      const ratio = Math.max(.18, stock / stockMaximum);
      const warningStartRatio = .58;
      const dangerRatio = .5;
      const dangerProgress = Math.min(1, Math.max(0, (warningStartRatio - ratio) / (warningStartRatio - dangerRatio)));
      const warningColor = [242, 190, 0];
      const dangerColor = [184, 77, 64];
      const stockColor = !hasLiveSignals
        ? dangerColor
        : warningColor.map((channel, index) => (
          Math.round(channel + ((dangerColor[index] - channel) * dangerProgress))
        ));

      stockBar?.style.setProperty('--nuora-stock-ratio', ratio.toFixed(3));
      stockBar?.style.setProperty('--nuora-stock-color', `rgb(${stockColor.join(', ')})`);
      stockBar?.style.setProperty('--nuora-stock-glow', `rgba(${stockColor.join(', ')}, .24)`);
      stockTrack?.setAttribute('aria-valuenow', String(stock));
      stockTrack?.setAttribute('aria-valuemax', String(stockMaximum));

      if (stock <= stockFloor) {
        if (stockStatus) stockStatus.textContent = 'Almost sold out';
        if (stockPrefix) stockPrefix.textContent = 'Only ';
        if (stockSuffix) stockSuffix.textContent = 'bottles left';
      } else if (stock <= 21) {
        if (stockStatus) stockStatus.textContent = 'Selling quickly';
        if (stockPrefix) stockPrefix.textContent = '';
        if (stockSuffix) stockSuffix.textContent = 'bottles available';
      } else {
        if (stockStatus) stockStatus.textContent = 'Limited batch availability';
        if (stockPrefix) stockPrefix.textContent = '';
        if (stockSuffix) stockSuffix.textContent = 'bottles available';
      }

      if (announce && announcement) announcement.textContent = `${stock} bottles currently available.`;
    };

    const clearTimers = () => {
      window.clearTimeout(stockTimer);
      window.clearTimeout(viewerTimer);
      stockTimer = 0;
      viewerTimer = 0;
    };

    const scheduleViewerUpdate = (delay = 3200) => {
      window.clearTimeout(viewerTimer);
      viewerTimer = window.setTimeout(() => {
        viewerTimer = 0;
        if (!visible || document.hidden || viewers >= viewerMaximum) return;
        viewers = Math.min(viewerMaximum, viewers + 1);
        animateNumber(viewerCount, viewers);
        if (viewers < viewerMaximum) scheduleViewerUpdate(7200 + Math.floor(Math.random() * 3600));
      }, delay);
    };

    const scheduleStockUpdate = (delay = 1800) => {
      window.clearTimeout(stockTimer);
      stockTimer = window.setTimeout(() => {
        stockTimer = 0;
        if (!visible || !engaged || document.hidden || stock <= stockFloor) return;
        const inInitialDrop = stock > previewRedThreshold;
        const stockStep = Math.random() < (inInitialDrop ? .32 : .12) ? 2 : 1;
        stock = Math.max(inInitialDrop ? previewRedThreshold : stockFloor, stock - stockStep);
        renderStock(true);
        if (stock > stockFloor) {
          scheduleStockUpdate(stock > previewRedThreshold
            ? 2400 + Math.floor(Math.random() * 1400)
            : 14000 + Math.floor(Math.random() * 4000));
        }
      }, delay);
    };

    const applyLiveSignals = (next) => {
      if (!next) return;
      if (Number.isFinite(Number(next.stock))) stock = Math.max(0, Number(next.stock));
      if (Number.isFinite(Number(next.viewers))) viewers = Math.max(0, Number(next.viewers));
      if (Number.isFinite(Number(next.stockMaximum))) stockMaximum = Math.max(stock, Number(next.stockMaximum));
      renderStock(true);
      animateNumber(viewerCount, viewers);
    };

    const startIfEligible = () => {
      clearTimers();
      if (!animateSignals || hasLiveSignals || !visible || document.hidden) return;
      if (viewers < viewerMaximum) scheduleViewerUpdate();
      if (engaged && stock > stockFloor) {
        scheduleStockUpdate(stock > previewRedThreshold ? 1800 : 4400);
      }
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= .5);
        startIfEligible();
      }, { threshold: [0, .5] });
      observer.observe(module);
      root.nuoraSignalsObserver = observer;
    } else {
      visible = true;
    }

    const markEngaged = () => {
      if (engaged) return;
      engaged = true;
      /* Pointer/scroll engagement is a stronger signal than a delayed
         IntersectionObserver callback, particularly inside Shopify preview
         overlays that temporarily lock or proxy the page scroll. */
      visible = true;
      startIfEligible();
    };
    window.addEventListener('scroll', markEngaged, { passive: true, once: true });
    window.addEventListener('touchstart', markEngaged, { passive: true, once: true });
    window.addEventListener('pointerdown', markEngaged, { passive: true, once: true });
    document.addEventListener('visibilitychange', startIfEligible);

    stockBar?.style.setProperty('--nuora-stock-ratio', Math.max(.18, stock / stockMaximum).toFixed(3));
    animateNumber(viewerCount, viewers);
    renderStock(false);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => renderStock(false));
    });
    if (hasLiveSignals && typeof signals.subscribe === 'function') {
      unsubscribe = signals.subscribe(applyLiveSignals);
    }
    root.nuoraSignalsCleanup = () => {
      clearTimers();
      root.nuoraSignalsObserver?.disconnect();
      document.removeEventListener('visibilitychange', startIfEligible);
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  };

  const initRoot = (scope = document) => {
    scope.querySelectorAll('nuora-pdp-390.nuora-gr-v3--hero').forEach(initOfferSignals);
    scope.querySelectorAll('[data-gr-v3-facebook]').forEach(initFacebook);
    scope.querySelectorAll('[data-gr-v3-accordion]').forEach(initAccordion);
    scope.querySelectorAll('[data-gr-v3-progress]').forEach(initProgress);
    scope.querySelectorAll('[data-gr-v3-review-grid]').forEach(initReviewGrid);
    scope.querySelectorAll('[data-gr-v3-amazon-reviews]').forEach(initAmazonReviews);
    scope.querySelectorAll('[data-gr-v3-ingredients]').forEach(initIngredients);
    scope.querySelectorAll('[data-gr-v3-footer]').forEach(initFooter);
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-gr-v3-bundle-jump]');
    if (!trigger) return;
    const offer = document.getElementById('offer');
    if (!offer) return;
    event.preventDefault();
    offer.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
  });

  document.addEventListener('shopify:section:load', (event) => initRoot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('nuora-pdp-390.nuora-gr-v3--hero').forEach((root) => root.nuoraSignalsCleanup?.());
    event.target.querySelectorAll('[data-gr-v3-progress]').forEach((root) => root.grV3Observer?.disconnect());
    event.target.querySelectorAll('[data-gr-v3-footer]').forEach((root) => root.grV3FooterCleanup?.());
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initRoot(), { once: true });
  } else {
    initRoot();
  }

  window.NuoraGrV3 = { init: initRoot };
})();
